import { RequisitionFormData, TestType } from '@/types/RequisitionFormTypes'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

interface RadyIntegrationProps {
  // Accept form data if passed as props
  formData?: RequisitionFormData
}

const RadyIntegration: React.FC<RadyIntegrationProps> = ({ formData: propFormData }) => {
  const location = useLocation()
  const [apiEndpoint, setApiEndpoint] = useState('https://api.rady.ucsd.edu/orders')

  // Get form data from location state or props
  const formData = propFormData || location.state?.formData || {}

  // Helper function to determine if parent sections should be shown
  const shouldShowParentSections = () => {
    const selectedOption = formData.order?.selectedOption as string
    return selectedOption !== TestType.rWGS_Patient_Only && selectedOption !== TestType.NONE
  }

  // Structure the complete order information
  const orderData = {
    // Order metadata
    orderId: `INF-${Date.now()}`,
    timestamp: new Date().toISOString(),
    provider: {
      name: formData.attestation?.providerName || 'Provider Inflection Medicine',
      email: formData.attestation?.providerEmail || '',
      institution: 'Inflection Medicine',
      npi: '1234567890', // Would come from FHIR data
    },

    // Order type selection
    orderType: {
      selectedOption: formData.order?.selectedOption || '',
      service: formData.order?.selectedOption?.includes('urWGS') ? 'Ultra Rapid WGS' : 'Rapid WGS',
      turnaroundTime: formData.order?.selectedOption?.includes('urWGS') ? '3 calendar days' : '5-7 business days',
    },

    // Patient information
    patient: {
      id: formData.patient?.id || '',
      name: formData.patient?.name || '',
      dateOfBirth: formData.patient?.dateOfBirth || '',
      gender: formData.patient?.gender || '',
      mrn: formData.patient?.mrn || '',
      contact: {
        phone: formData.patient?.phone || '',
        email: formData.patient?.email || '',
        address: formData.patient?.address || {},
      },
    },

    // Clinical information
    clinical: {
      notes: formData.clinical?.notes || '',
      optOut: formData.clinical?.optOut || false,
      hpoTerms: formData.reason?.clinicalDiagnosis || [],
      icd10Terms: formData.reason?.icd10Diagnosis || [],
      reasonForTesting: {
        userInteracted: formData.reason?.userInteracted || false,
        timestamp: new Date().toISOString(),
      },
    },

    // Family history
    familyHistory: {
      notes: formData.familyHistory?.notes || '',
    },

    // Biological parents information - only included when parent sections are required
    biologicalParents: shouldShowParentSections()
      ? {
          mother: {
            name: formData.mother?.name || '',
            dateOfBirth: formData.mother?.dateOfBirth || '',
            contact: formData.mother?.contact || {},
            medicalHistory: formData.mother?.medicalHistory || '',
            consentSigned: formData.mother?.consentSigned || false,
            hpoTerms: formData.mother?.hpoTerms || [],
            icd10Terms: formData.mother?.icd10Terms || [],
          },
          father: {
            name: formData.father?.name || '',
            dateOfBirth: formData.father?.dateOfBirth || '',
            contact: formData.father?.contact || {},
            medicalHistory: formData.father?.medicalHistory || '',
            consentSigned: formData.father?.consentSigned || false,
            hpoTerms: formData.father?.hpoTerms || [],
            icd10Terms: formData.father?.icd10Terms || [],
          },
        }
      : undefined,

    // Provider attestation
    attestation: {
      providerName: formData.attestation?.providerName || '',
      providerEmail: formData.attestation?.providerEmail || '',
      templateId: formData.attestation?.templateId || '',
      envelopeId: formData.attestation?.envelopeId || '',
      status: formData.attestation?.status || 'pending',
      isSigningComplete: formData.attestation?.isSigningComplete || false,
      signingUrl: formData.attestation?.signingUrl || '',
    },

    // Consent information
    consent: {
      parentalConsent: shouldShowParentSections() ? formData.consent?.parentalConsent || false : false,
      researchConsent: formData.consent?.researchConsent || false,
      dataSharing: formData.consent?.dataSharing || false,
    },

    // Submission metadata
    submission: {
      timestamp: new Date().toISOString(),
      status: 'draft',
      version: '1.0',
      source: 'Inflection Medicine Provider UI',
    },
  }

  // Generate CURL command
  const curlCommand = `curl -X POST "${apiEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "X-API-Version: 1.0" \\
  -d '${JSON.stringify(orderData, null, 2).replace(/'/g, "\\'")}'`

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert('Copied to clipboard!')
      })
      .catch(() => {
        alert('Failed to copy to clipboard')
      })
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-lg bg-white p-6 shadow-lg">
          {/* Header */}
          <div className="mb-6 border-b border-gray-200 pb-4">
            <h1 className="page-header">Rady Integration</h1>
            <p className="body-text mt-2 text-gray-600">
              Complete order information structured for Rady Children's Institute API integration
            </p>
          </div>

          {/* API Endpoint Configuration */}
          <div className="mb-6">
            <h2 className="section-header mb-3">API Endpoint Configuration</h2>
            <div className="flex items-center space-x-4">
              <label className="form-label">Endpoint URL:</label>
              <input
                type="text"
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://api.rady.ucsd.edu/orders"
              />
            </div>
          </div>

          {/* CURL Command */}
          <div className="mb-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-header">CURL Command</h2>
              <button
                onClick={() => copyToClipboard(curlCommand)}
                className="button-text rounded-md bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
              >
                Copy CURL
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-green-400">
              <pre className="whitespace-pre font-mono text-sm">{curlCommand}</pre>
            </div>
          </div>

          {/* JSON Structure */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-header">Order Data JSON</h2>
              <button
                onClick={() => copyToClipboard(JSON.stringify(orderData, null, 2))}
                className="button-text rounded-md bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700"
              >
                Copy JSON
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-800">{JSON.stringify(orderData, null, 2)}</pre>
            </div>
          </div>

          {/* Integration Notes */}
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h3 className="form-label mb-2 text-blue-800">Integration Notes</h3>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• This JSON structure includes all form data collected in the Test Requisition Form</li>
              <li>• The API endpoint can be customized for different environments (dev, staging, production)</li>
              <li>• Authentication token should be obtained from Rady's API documentation</li>
              <li>• All timestamps are in ISO 8601 format</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RadyIntegration
